import { Alert, Box, Button, Slide, Snackbar, Stack, Typography } from '@mui/material';
import Iconify from '../iconify';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import usePwaInstall from '../../hooks/usePwaInstall';
import { applyServiceWorkerUpdate, onServiceWorkerUpdate } from '../../serviceWorkerRegistration';
import { useEffect, useState } from 'react';

export default function PwaStatus() {
  const isOnline = useOnlineStatus();
  const {
    showInstallBanner,
    showIosHint,
    install,
    dismissAndroidPrompt,
    dismissIos,
  } = usePwaInstall();
  const [updateReady, setUpdateReady] = useState(false);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => onServiceWorkerUpdate(() => setUpdateReady(true)), []);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      return;
    }

    if (wasOffline) {
      setShowBackOnline(true);
      setWasOffline(false);
    }
  }, [isOnline, wasOffline]);

  const handleInstall = async () => {
    await install();
  };

  // Prefer the native install banner when available; otherwise show the iOS
  // Share → Add to Home Screen instructions. Never both at once.
  const showIosBanner = showIosHint && !showInstallBanner;

  return (
    <>
      {!isOnline && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            py: 0.75,
            px: 2,
            textAlign: 'center',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Iconify icon="eva:wifi-off-fill" width={18} height={18} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              You are offline. Scans will be queued and sent when the connection returns.
            </Typography>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={showBackOnline}
        autoHideDuration={4000}
        onClose={() => setShowBackOnline(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setShowBackOnline(false)}>
          Back online
        </Alert>
      </Snackbar>

      <Snackbar
        open={updateReady}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert
          severity="info"
          variant="filled"
          action={
            <Button color="inherit" size="small" onClick={applyServiceWorkerUpdate}>
              Refresh
            </Button>
          }
        >
          A new version is available
        </Alert>
      </Snackbar>

      <Snackbar
        open={showInstallBanner}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        sx={{ mb: { xs: 1, sm: 2 } }}
      >
        <Alert
          severity="info"
          icon={
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/favicon/android-chrome-192x192.png`}
              alt=""
              sx={{ width: 28, height: 28, borderRadius: 0.75 }}
            />
          }
          sx={{ alignItems: 'center' }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={dismissAndroidPrompt}>
                Not now
              </Button>
              <Button color="primary" variant="contained" size="small" onClick={handleInstall}>
                Install
              </Button>
            </Stack>
          }
        >
          Install City Courier for faster access and offline scanning
        </Alert>
      </Snackbar>

      <Snackbar
        open={showIosBanner}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        sx={{ mb: { xs: 1, sm: 2 } }}
      >
        <Alert
          severity="info"
          icon={
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/favicon/apple-touch-icon.png`}
              alt=""
              sx={{ width: 28, height: 28, borderRadius: 0.75 }}
            />
          }
          sx={{ alignItems: 'flex-start', maxWidth: 420 }}
          action={
            <Button color="inherit" size="small" onClick={dismissIos}>
              Got it
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Install City Courier on this iPhone
          </Typography>
          <Typography variant="caption" component="div" sx={{ display: 'block', lineHeight: 1.5 }}>
            Tap the Share button
            <Box component="span" sx={{ mx: 0.5, verticalAlign: 'middle', display: 'inline-flex' }}>
              <Iconify icon="eva:share-outline" width={16} height={16} />
            </Box>
            then choose <strong>Add to Home Screen</strong>.
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
}
